import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Stack,
  Checkbox,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { Gavel, Title as TitleIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import { getAuthToken } from '../../lib/authToken';

export interface CaptionDocument {
  id: string;
  title: string;
  fileName: string;
  mimeType?: string;
  metadata?: Record<string, unknown>;
}

interface AutoCaptionDialogProps {
  open: boolean;
  onClose: () => void;
  caseId: string;
  documents: CaptionDocument[];
  onSuccess?: () => void;
}

export function getCaptionDisplay(metadata?: Record<string, unknown> | null): string | null {
  if (!metadata) return null;
  const raw = metadata.caption;
  if (!raw) return null;
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object' && raw !== null && (raw as { text?: string }).text) {
    return String((raw as { text: string }).text);
  }
  return null;
}

const AutoCaptionDialog: React.FC<AutoCaptionDialogProps> = ({
  open,
  onClose,
  caseId,
  documents,
  onSuccess,
}) => {
  const [style, setStyle] = useState('short');
  const [captionText, setCaptionText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [plaintiffNames, setPlaintiffNames] = useState<string[]>([]);
  const [adverseNames, setAdverseNames] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [updateTitle, setUpdateTitle] = useState(false);
  const [updateDescription, setUpdateDescription] = useState(true);
  const [stampPdfs, setStampPdfs] = useState(false);
  const [stampPosition, setStampPosition] = useState('top-center');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadPreview = async (nextStyle = style, override?: string) => {
    setPreviewLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/cases/${caseId}/caption/generate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          style: nextStyle,
          overrideText: override !== undefined ? override : null,
        }),
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Unable to generate caption.'));
      const json = await res.json();
      const caption = json.data?.caption;
      setCaptionText(caption?.text || '');
      setPlaintiffNames(caption?.plaintiffNames || []);
      setAdverseNames(caption?.adverseNames || []);
    } catch (err) {
      setError(toUserErrorMessage(err, 'Unable to generate caption.'));
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setStyle('short');
    setSelectedIds([]);
    setUpdateTitle(false);
    setUpdateDescription(true);
    setStampPdfs(false);
    setStampPosition('top-center');
    setError('');
    loadPreview('short');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, caseId]);

  const toggleDoc = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (!captionText.trim()) {
      setError('Caption text is required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`/api/cases/${caseId}/caption/apply`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          style,
          overrideText: captionText.trim(),
          documentIds: selectedIds,
          updateTitle,
          updateDescription,
          stampPdfs,
          stampPosition,
        }),
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Unable to apply caption.'));
      const json = await res.json();
      const stamped = (json.data?.applied || []).filter((a: any) => a.caption?.stamped).length;
      toast.success(
        stamped > 0
          ? `Caption saved — ${stamped} PDF(s) stamped`
          : selectedIds.length
            ? `Caption saved to case and ${selectedIds.length} document(s)`
            : 'Caption saved to case'
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      const msg = toUserErrorMessage(err, 'Unable to apply caption.');
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Gavel color="primary" />
        Auto-caption
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <Typography variant="body2" color="text.secondary">
            Build a case caption from the client and adverse parties. Saves to the matter for
            template merge (`Case_Caption`, `Adverse_Party_Name`) and can apply to documents.
          </Typography>

          <FormControl fullWidth size="small">
            <InputLabel>Style</InputLabel>
            <Select
              label="Style"
              value={style}
              onChange={(e) => {
                const next = e.target.value;
                setStyle(next);
                loadPreview(next);
              }}
            >
              <MenuItem value="short">Short (et al. when 3+)</MenuItem>
              <MenuItem value="et_al">Always et al. when 3+</MenuItem>
              <MenuItem value="full">Full party list</MenuItem>
            </Select>
          </FormControl>

          {(plaintiffNames.length > 0 || adverseNames.length > 0) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {plaintiffNames.map((n) => (
                <Chip key={`p-${n}`} size="small" color="primary" variant="outlined" label={`P: ${n}`} />
              ))}
              {adverseNames.map((n) => (
                <Chip key={`a-${n}`} size="small" color="warning" variant="outlined" label={`D: ${n}`} />
              ))}
            </Box>
          )}

          <TextField
            fullWidth
            label="Caption"
            value={captionText}
            onChange={(e) => setCaptionText(e.target.value)}
            multiline
            minRows={2}
            disabled={previewLoading}
            InputProps={{
              endAdornment: previewLoading ? <CircularProgress size={18} /> : null,
            }}
            helperText="Edit before saving if counsel prefers a different form."
          />

          <Typography variant="subtitle2">Apply to documents (optional)</Typography>
          {documents.length === 0 ? (
            <Alert severity="info">No documents in this folder — caption will still save to the case.</Alert>
          ) : (
            <List
              dense
              sx={{ maxHeight: 180, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}
            >
              {documents.map((doc) => {
                const existing = getCaptionDisplay(doc.metadata);
                return (
                  <ListItem key={doc.id} disablePadding>
                    <FormControlLabel
                      sx={{ width: '100%', mx: 1 }}
                      control={
                        <Checkbox
                          checked={selectedIds.includes(doc.id)}
                          onChange={() => toggleDoc(doc.id)}
                        />
                      }
                      label={
                        <ListItemText
                          primary={doc.title || doc.fileName}
                          secondary={existing ? `Current: ${existing}` : doc.fileName}
                        />
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}

          <FormControlLabel
            control={
              <Switch checked={updateDescription} onChange={(e) => setUpdateDescription(e.target.checked)} />
            }
            label="Append caption to selected document descriptions"
            disabled={selectedIds.length === 0}
          />
          <FormControlLabel
            control={<Switch checked={updateTitle} onChange={(e) => setUpdateTitle(e.target.checked)} />}
            label="Replace selected document titles with caption"
            disabled={selectedIds.length === 0}
          />
          <FormControlLabel
            control={<Switch checked={stampPdfs} onChange={(e) => setStampPdfs(e.target.checked)} />}
            label="Stamp caption on PDF pages (new version)"
            disabled={selectedIds.length === 0}
          />
          {stampPdfs && (
            <FormControl fullWidth size="small">
              <InputLabel>Stamp position</InputLabel>
              <Select
                label="Stamp position"
                value={stampPosition}
                onChange={(e) => setStampPosition(e.target.value)}
              >
                <MenuItem value="top-center">Top center</MenuItem>
                <MenuItem value="top-left">Top left</MenuItem>
                <MenuItem value="top-right">Top right</MenuItem>
                <MenuItem value="bottom-center">Bottom center</MenuItem>
              </Select>
            </FormControl>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <TitleIcon />}
          onClick={handleSubmit}
          disabled={submitting || !captionText.trim()}
        >
          {submitting ? 'Saving…' : 'Save caption'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoCaptionDialog;
